import type { FC } from 'react';
import { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl';

import type {
  FilteredHeatMapData,
  HeatMapSexFilterOption,
  HeatMapRange,
  HeatMapRangeKey,
  HeatMapStatusFilterOption,
  HeatMapFilterOptions,
} from '../../map-data/map-data-types';
import type { HeatMapLegend } from '../../map-data/heatmap-legend-config';
import {
  heatMapLegends,
  nccpLegendConfig,
  getNccpColorAtYear,
  NCCP_TIMELINE_START_YEAR,
  countryStatusConfig,
  getStatusColorAtYear,
} from '../../map-data/heatmap-legend-config';
import { useMapData } from '../../map-data/use-map-data';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';

type Props = {
  filterTypeSex: HeatMapSexFilterOption;
  rangeType: HeatMapRange;
  filterTypeStatus: HeatMapStatusFilterOption;
  hoveredIso2: string | null;
  selectedIso2: string | null;
  filterOptions: HeatMapFilterOptions;
};

const generateLayerProps = (
  key: HeatMapRangeKey,
  filteredMapData: FilteredHeatMapData,
  legendConfig: HeatMapLegend,
) => {
  const countries = filteredMapData[key];
  return {
    filter: [
      'all',
      [
        'any',
        ['==', 'all', ['get', 'worldview']],
        ['in', 'US', ['get', 'worldview']],
      ],
      [
        'match',
        ['get', 'iso_3166_1_alpha_3'],
        countries.length > 0 ? countries : [''],
        true,
        false,
      ],
    ],

    id: `country-boundaries-${key}`,
    'source-layer': 'country_boundaries',
    beforeId: 'admin-0-boundary',
    paint: {
      'fill-color': legendConfig[key].color,
      'fill-opacity': 1,
    },
    type: 'fill' as const,
  };
};

export const HeatMapSource: FC<Props> = ({
  rangeType,
  filterTypeSex,
  filterTypeStatus,
  filterOptions,
  hoveredIso2,
  selectedIso2,
}) => {
  const { countryHeatMapData, countryHeatMapMetaData, loading } = useMapData();
  const { filteredCountryMetaData } = useHeatMapFilterOptions();

  // Create a Set of ISO3 codes for filtered countries for quick lookup
  const filteredCountryIso3Set = useMemo(() => {
    return new Set(filteredCountryMetaData.map(c => c.countryIso3166_1_3Name));
  }, [filteredCountryMetaData]);

  const worldviewFilter = [
    'any',
    ['==', 'all', ['get', 'worldview']],
    ['in', 'US', ['get', 'worldview']],
  ] as const;

  // Group countries by their NCCP status color for NCCP mode
  // Color is now time-dependent based on the selected year
  // Only includes countries that pass the active filters (e.g., region)
  const nccpCountryGroupsByColor = useMemo(() => {
    if (rangeType !== 'nccp' || !countryHeatMapMetaData) return {};

    const groups: Record<string, string[]> = {};
    // Default to current year if no year filter set
    const selectedYear = filterOptions.nccpYearMax ?? new Date().getFullYear();

    for (const country of Object.values(countryHeatMapMetaData)) {
      const iso3 = country.countryIso3166_1_3Name;

      // Skip countries not in the filtered list
      if (!filteredCountryIso3Set.has(iso3)) continue;

      // Get the color at the selected year (returns null if country not visible yet)
      const color = getNccpColorAtYear(country, selectedYear);

      // Skip countries not yet visible at this year
      if (color === null) continue;

      if (!groups[color]) groups[color] = [];
      groups[color].push(iso3);
    }

    return groups;
  }, [
    rangeType,
    countryHeatMapMetaData,
    filterOptions,
    filteredCountryIso3Set,
  ]);

  // Group countries by their status color for Status mode
  // Color is time-dependent based on the selected year
  // Only includes countries that pass the active filters (e.g., region, NCCP filters)
  const statusCountryGroupsByColor = useMemo(() => {
    if (rangeType !== 'status' || !countryHeatMapMetaData) return {};

    const groups: Record<string, string[]> = {};
    // Default to current year if no year filter set
    const selectedYear =
      filterOptions.statusYearMax ?? new Date().getFullYear();

    for (const country of Object.values(countryHeatMapMetaData)) {
      const iso3 = country.countryIso3166_1_3Name;

      // Skip countries not in the filtered list
      if (!filteredCountryIso3Set.has(iso3)) continue;

      // Get the color at the selected year
      const color = getStatusColorAtYear(country, selectedYear);

      if (!groups[color]) groups[color] = [];
      groups[color].push(iso3);
    }

    return groups;
  }, [
    rangeType,
    countryHeatMapMetaData,
    filterOptions,
    filteredCountryIso3Set,
  ]);

  if (!countryHeatMapData || loading) {
    // handle loading state here.
    return null;
  }

  // Handle NCCP mode with separate rendering logic
  if (rangeType === 'nccp') {
    return (
      <Source
        key="nccp-source"
        type="vector"
        url="mapbox://mapbox.country-boundaries-v1"
      >
        {nccpLegendConfig.map(item => {
          const countries = nccpCountryGroupsByColor[item.color] || [];

          return (
            <Layer
              key={`nccp-${item.translationKey}`}
              id={`country-boundaries-nccp-${item.translationKey}`}
              type="fill"
              source-layer="country_boundaries"
              beforeId="admin-0-boundary"
              filter={[
                'all',
                worldviewFilter,
                [
                  'match',
                  ['get', 'iso_3166_1_alpha_3'],
                  countries.length > 0 ? countries : [''],
                  true,
                  false,
                ],
              ]}
              paint={{
                'fill-color': item.color,
                'fill-opacity': 1,
              }}
            />
          );
        })}

        {hoveredIso2 && (
          <Layer
            id="country-hover-outline-nccp"
            type="line"
            source-layer="country_boundaries"
            filter={[
              'all',
              worldviewFilter,
              ['==', ['get', 'iso_3166_1'], hoveredIso2],
            ]}
            paint={{
              'line-width': 1,
              'line-color': '#FFF',
            }}
          />
        )}
        {selectedIso2 && (
          <Layer
            id="country-select-outline-nccp"
            type="line"
            source-layer="country_boundaries"
            filter={[
              'all',
              worldviewFilter,
              ['==', ['get', 'iso_3166_1'], selectedIso2],
            ]}
            paint={{
              'line-width': 2,
              'line-color': '#FFF',
            }}
          />
        )}
      </Source>
    );
  }

  // Handle Status mode with separate rendering logic
  if (rangeType === 'status') {
    return (
      <Source
        key="status-source"
        type="vector"
        url="mapbox://mapbox.country-boundaries-v1"
      >
        {countryStatusConfig.map(item => {
          const countries = statusCountryGroupsByColor[item.color] || [];

          return (
            <Layer
              key={`status-${item.translationKey}`}
              id={`country-boundaries-status-${item.translationKey}`}
              type="fill"
              source-layer="country_boundaries"
              beforeId="admin-0-boundary"
              filter={[
                'all',
                worldviewFilter,
                [
                  'match',
                  ['get', 'iso_3166_1_alpha_3'],
                  countries.length > 0 ? countries : [''],
                  true,
                  false,
                ],
              ]}
              paint={{
                'fill-color': item.color,
                'fill-opacity': 1,
              }}
            />
          );
        })}

        {hoveredIso2 && (
          <Layer
            id="country-hover-outline-status"
            type="line"
            source-layer="country_boundaries"
            filter={[
              'all',
              worldviewFilter,
              ['==', ['get', 'iso_3166_1'], hoveredIso2],
            ]}
            paint={{
              'line-width': 1,
              'line-color': '#FFF',
            }}
          />
        )}
        {selectedIso2 && (
          <Layer
            id="country-select-outline-status"
            type="line"
            source-layer="country_boundaries"
            filter={[
              'all',
              worldviewFilter,
              ['==', ['get', 'iso_3166_1'], selectedIso2],
            ]}
            paint={{
              'line-width': 2,
              'line-color': '#FFF',
            }}
          />
        )}
      </Source>
    );
  }

  // Standard incidence/mortality rendering
  const legendConfig = heatMapLegends[rangeType][filterTypeSex];

  // Handle multiple status filtering with OR logic
  const getFilteredMapData = (): FilteredHeatMapData => {
    const statusArray = Array.isArray(filterOptions.status)
      ? filterOptions.status
      : [filterTypeStatus];

    // If no specific statuses are selected (empty array), show all
    const statusesToFilter =
      statusArray.length === 0 ? ['AllStatuses'] : statusArray;

    // Combine data from multiple status filters
    const combinedData: FilteredHeatMapData = {
      60: [],
      50: [],
      40: [],
      30: [],
      20: [],
      10: [],
    };

    for (const status of statusesToFilter) {
      const dataKey =
        `${rangeType}${filterTypeSex}${status}` as keyof typeof countryHeatMapData;
      const statusFilteredData = countryHeatMapData?.[dataKey];
      if (statusFilteredData) {
        // Merge country codes for each range key using OR logic (union)
        for (const key of Object.keys(combinedData)) {
          const rangeKey = Number.parseInt(key) as HeatMapRangeKey;
          const existingCountries = new Set(combinedData[rangeKey]);
          const newCountries = statusFilteredData[rangeKey] || [];

          for (const country of newCountries) {
            existingCountries.add(country);
          }
          combinedData[rangeKey] = [...existingCountries];
        }
      }
    }

    const {
      continent,
      nccpAvailable,
      lungCancerInNccp,
      earlyDetectionInNccp,
      fundingForEarlyDetection,
      nccpYearMax,
    } = filterOptions;

    const hasAdditionalFilters =
      continent ||
      nccpAvailable ||
      lungCancerInNccp ||
      earlyDetectionInNccp ||
      fundingForEarlyDetection ||
      nccpYearMax !== undefined;

    if (hasAdditionalFilters && countryHeatMapMetaData) {
      for (const key of Object.keys(combinedData)) {
        const rangeKey = Number.parseInt(key) as HeatMapRangeKey;
        combinedData[rangeKey] = combinedData[rangeKey].filter(countryCode => {
          const countryData = Object.values(countryHeatMapMetaData).find(
            country => country.countryIso3166_1_3Name === countryCode,
          );

          if (!countryData) return false;

          if (
            continent &&
            (!countryData.continent || countryData.continent !== continent)
          ) {
            return false;
          }

          if (nccpAvailable && countryData.nccpAvailable !== nccpAvailable) {
            return false;
          }

          if (
            lungCancerInNccp &&
            countryData.lungCancerInNccp !== lungCancerInNccp
          ) {
            return false;
          }

          if (
            earlyDetectionInNccp &&
            countryData.earlyDetectionInNccp !== earlyDetectionInNccp
          ) {
            return false;
          }

          if (
            fundingForEarlyDetection &&
            countryData.fundingForEarlyDetection !== fundingForEarlyDetection
          ) {
            return false;
          }

          if (nccpYearMax !== undefined) {
            const yearStr = countryData.previousNccpPublished;
            // Countries without a valid previousNccpPublished appear from 1997
            if (!yearStr || !/^\d{4}$/.test(yearStr)) {
              if (nccpYearMax < NCCP_TIMELINE_START_YEAR) {
                return false;
              }
            } else {
              const year = Number.parseInt(yearStr, 10);
              if (year > nccpYearMax) {
                return false;
              }
            }
          }

          return true;
        });
      }
    }

    return combinedData;
  };

  const filteredMapData = getFilteredMapData();

  return (
    <Source
      key="standard-source"
      type="vector"
      url="mapbox://mapbox.country-boundaries-v1"
    >
      <Layer {...generateLayerProps(60, filteredMapData, legendConfig)} />
      <Layer {...generateLayerProps(50, filteredMapData, legendConfig)} />
      <Layer {...generateLayerProps(40, filteredMapData, legendConfig)} />
      <Layer {...generateLayerProps(30, filteredMapData, legendConfig)} />
      <Layer {...generateLayerProps(20, filteredMapData, legendConfig)} />
      <Layer {...generateLayerProps(10, filteredMapData, legendConfig)} />

      {hoveredIso2 && (
        <Layer
          id="country-hover-outline"
          type="line"
          source-layer="country_boundaries"
          filter={[
            'all',
            worldviewFilter,
            ['==', ['get', 'iso_3166_1'], hoveredIso2],
          ]}
          paint={{
            'line-width': 1,
            'line-color': '#FFF', // subtle dark outline; change if you want
          }}
        />
      )}
      {selectedIso2 && (
        <Layer
          id="country-select-outline"
          type="line"
          source-layer="country_boundaries"
          filter={[
            'all',
            worldviewFilter,
            ['==', ['get', 'iso_3166_1'], selectedIso2],
          ]}
          paint={{
            'line-width': 2,
            'line-color': '#FFF', // subtle dark outline; change if you want
          }}
        />
      )}
    </Source>
  );
};
